import { useMemo, useState } from 'react';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import { listConversations } from '@/lib/messages.ts';
import type { ConversationOverview } from '@/types/messages.ts';
import { attentionOf } from '@/components/conversationReview/attentionOf.ts';
import {
  findGroup,
  groupConversations,
  type ConversationGroup,
} from './conversationGroups.ts';
import type { MessagesPreferences } from './useMessagesPreferences.ts';

export interface ConversationChoice {
  conversations: UseQueryResult<ConversationOverview[]>;
  groups: ConversationGroup[];
  /** The conversation, or channel, being read. */
  selection: ConversationGroup | null;
  choose: (group: ConversationGroup) => void;
  /** Back to where a person opens: what was chosen or linked no longer applies. */
  forget: () => void;
  /** Groups by channel or not, and opens the person where they open. */
  changeGrouping: (byChannel: boolean) => void;
}

/** One person's conversations, grouped as asked, and which of them is being read. */
export function useConversationChoice(
  userId: string | null,
  linkedConversation: string | undefined,
  preferences: MessagesPreferences,
): ConversationChoice {
  const { groupByChannel } = preferences;
  const [chosen, setChosen] = useState<string | null>(null);
  /*
   * Which conversation to land on when nothing has been chosen: the one the
   * link named, until a person or a grouping change makes it meaningless.
   */
  const [preferred, setPreferred] = useState<string | null>(linkedConversation ?? null);

  const conversations = useQuery({
    queryKey: ['messagesConversations', userId],
    queryFn: () => listConversations(userId!),
    enabled: Boolean(userId),
  });

  const groups = useMemo(
    () => groupConversations(conversations.data ?? [], groupByChannel),
    [conversations.data, groupByChannel],
  );

  /*
   * What is being read: what was chosen, while it still exists among these
   * groups; otherwise the conversation the link named; otherwise the most
   * recent one with open findings, which is what there is to look at; otherwise
   * the most recent one.
   */
  const selection =
    findGroup(groups, chosen) ??
    findGroup(groups, preferred) ??
    groups.find(hasOpenFindings) ??
    groups[0] ??
    null;

  function choose(group: ConversationGroup) {
    setPreferred(null);
    setChosen(group.id);
  }

  function forget() {
    setPreferred(null);
    setChosen(null);
  }

  function changeGrouping(byChannel: boolean) {
    /* Back to where a person opens, as the Vue view did when this was switched. */
    forget();
    preferences.changeGroupByChannel(byChannel);
  }

  return { conversations, groups, selection, choose, forget, changeGrouping };
}

function hasOpenFindings(group: ConversationGroup): boolean {
  return (
    attentionOf(
      group.conversations.flatMap((conversation) =>
        conversation.open_reviews.flatMap((review) =>
          review.findings.map((finding) => finding.severity),
        ),
      ),
    ) !== null
  );
}
